import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import withStyles from '@material-ui/core/styles/withStyles';
import {
    Box,
    ButtonBase,
    Typography,
    Fade,
    MenuItem,
    Fab,
    Popper,
    Grow,
    Paper,
    ClickAwayListener,
    MenuList
} from '@material-ui/core';
// import HowToVoteIcon from '@material-ui/icons/HowToVote';
import SearchIcon from '@material-ui/icons/Search';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import LandRegistryLogo from '../../../../../assets/images/LandRegistry_Logo.png';

class Notification extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        notification: PropTypes.object.isRequired,
        onNotificationClick: PropTypes.func,
        onNotificationRead: PropTypes.func.isRequired,
        onNotificationUnread: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            isHovered: false,
            menuAnchorEl: null,
            arrowRef: null
        };

        this.handleMouseOver = this.handleMouseOver.bind(this);
        this.handleMouseOut = this.handleMouseOut.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleMenuOpen = this.handleMenuOpen.bind(this);
        this.handleMenuClose = this.handleMenuClose.bind(this);
        this.formatDateTime = this.formatDateTime.bind(this);
        this.toggleRead = this.toggleRead.bind(this);
        this.getIcon = this.getIcon.bind(this);
    }

    handleMouseOver() {
        this.setState({
            isHovered: true
        });
    }

    handleMouseOut() {
        this.setState({
            isHovered: false
        });
    }

    handleClick() {
        const { notification, onNotificationClick, onNotificationRead } = this.props;
        if (onNotificationClick) {
            onNotificationClick(notification);
        }
        if (!notification.read) {
            onNotificationRead(notification.id);
        }
    }

    handleMenuOpen(event) {
        event.stopPropagation();
        this.setState({
            menuAnchorEl: event.currentTarget
        });
    }

    handleMenuClose() {
        this.setState({
            menuAnchorEl: null
        });
    }

    formatDateTime(datetime) {
        const diffMilliSecs = Date.now() - Date.parse(datetime);
        const seconds = diffMilliSecs / 1000;
        if (seconds < 60) {
            return `${Math.floor(seconds)}s`;
        }
        const minutes = seconds / 60;
        if (minutes < 60) {
            return `${Math.floor(minutes)}m`;
        }
        const hours = minutes / 60;
        if (hours < 48) {
            return `${Math.floor(hours)}h`;
        }
        const days = hours / 24;
        if (days < 365) {
            return `${Math.floor(days)}d`;
        }
        return 'over 1 year';
    }

    toggleRead() {
        const { notification, onNotificationRead, onNotificationUnread } = this.props;
        notification.read
            ? onNotificationUnread(notification.id)
            : onNotificationRead(notification.id);
        this.handleMenuClose();
    }

    getIcon() {
        const { notification } = this.props;
        switch (notification.notificationType) {
            case 'search':
                return <SearchIcon fontSize="small" />;
            case 'analysis':
                return <FormatListNumberedIcon fontSize="small" />;
            case 'planning_report':
                return <FormatListNumberedIcon fontSize="small" />;
            case 'paid_order':
            case 'fulfill_order':
                return <img src={LandRegistryLogo} />;
            default:
                console.error('Unidentified notification type');
                return <></>;
        }
    }

    // Why the arrow UI is not built into the popper, who knows.
    setArrowRef = ref => {
        this.setState({ arrowRef: ref });
    };

    render() {
        const { classes, notification } = this.props;
        const { title, message, read, createdAt } = notification;
        const { isHovered, menuAnchorEl } = this.state;

        return (
            <ButtonBase
                className={classnames(classes.root, !notification.read && classes.rootUnread)}
                component="div"
                onClick={this.handleClick}
                onMouseOver={this.handleMouseOver}
                onMouseOut={this.handleMouseOut}>
                <Box color="text.primary" className={classes.header}>
                    {this.getIcon()}
                    <Typography className={classes.title} variant="subtitle1">
                        {title}
                    </Typography>
                    {!notification.read && <div className={classes.badge} />}
                    <Typography className={classes.time} variant="caption">
                        {this.formatDateTime(createdAt)} ago
                    </Typography>
                </Box>
                <Typography className={classes.message} color="textPrimary" variant="body2">
                    {message}
                </Typography>

                <Fade in={isHovered || Boolean(menuAnchorEl)}>
                    <div className={classes.fabContainer}>
                        <Fab size="small" color="primary" onClick={this.handleMenuOpen}>
                            <MoreVertIcon />
                        </Fab>
                    </div>
                </Fade>
                <Popper
                    className={classes.popper}
                    open={Boolean(menuAnchorEl)}
                    anchorEl={menuAnchorEl}
                    transition
                    placement="bottom-end"
                    popperOptions={{
                        modifiers: {
                            offset: {
                                offset: '0,10'
                            },
                            arrow: {
                                enabled: true,
                                element: this.arrowRef
                            }
                        }
                    }}>
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                            <>
                                <Paper>
                                    <span ref={this.setArrowRef} className={classes.arrow} />
                                    <ClickAwayListener onClickAway={this.handleMenuClose}>
                                        <MenuList autoFocusItem={Boolean(menuAnchorEl)}>
                                            <MenuItem onClick={this.toggleRead}>
                                                {read ? 'Mark as unread' : 'Mark as read'}
                                            </MenuItem>
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </>
                        </Grow>
                    )}
                </Popper>
            </ButtonBase>
        );
    }
}

const ARROW_SIZE = 10;
const styles = theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: `${theme.constants.spacing.md}px`,
        alignItems: 'flex-start',
        boxSizing: 'border-box'
    },
    rootUnread: {
        // backgroundColor: theme.palette.background.dark
    },
    header: {
        width: 'inherit',
        display: 'flex',
        alignItems: 'center',
        marginBottom: `${theme.constants.spacing.md}px`,
        '& > *:not(:last-child)': {
            marginRight: `${theme.constants.spacing.sm}px`
        }
    },
    title: {
        paddingTop: '2px' // Dirty style to make alignment look nicer
    },
    message: {
        whiteSpace: 'pre-line'
    },
    badge: {
        borderRadius: '50%',
        width: 8,
        height: 8,
        backgroundColor: theme.palette.error.main
    },
    time: {
        flexShrink: 0,
        marginLeft: 'auto',
        color: theme.palette.grey.variant3
    },
    fabContainer: {
        position: 'absolute',
        right: 10,
        top: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    },
    popper: {
        zIndex: theme.zIndex.popover + 1,
        '&[x-placement*="bottom"] $arrow': {
            top: `-${ARROW_SIZE / 2}px`,
            right: `${20 - ARROW_SIZE / 2}px`
        }
    },
    arrow: {
        position: 'absolute',
        width: `${ARROW_SIZE}px`,
        height: `${ARROW_SIZE}px`,
        background: 'inherit',
        visibility: 'hidden',
        '&::before': {
            position: 'inherit',
            width: 'inherit',
            height: 'inherit',
            background: 'inherit',
            visibility: 'visible',
            content: '""',
            transform: 'rotate(45deg)'
        }
    }
});

export default withStyles(styles)(Notification);
